import React, { useState, useEffect, useRef } from 'react';
import * as api from '../../../api/contentStudioV2';
import { useLanguage } from '../../../contexts/LanguageContext';
import { 
  validateAudienceForm, 
  getErrorMessage, 
  scrollToFirstError 
} from '../utils/formValidation';
import { 
  getStateSlangSuggestions, 
  validateSlang, 
  MAX_SLANG_COUNT 
} from '../../../constants/stateSlangMap';
import '../styles/formErrors.css';

// Malaysian states list
const MALAYSIAN_STATES = [
  "All States",
  "Johor", "Kedah", "Kelantan", "Melaka", "Negeri Sembilan", "Pahang",
  "Perak", "Perlis", "Pulau Pinang", "Selangor", "Terengganu",
  "Kuala Lumpur", "Putrajaya", "Labuan", "Sabah", "Sarawak"
];

// Age ranges (fixed set)
const AGE_RANGES = ["13-17", "18-24", "25-34", "35-44", "45-54", "55-64", "65+"];

const Step2TargetedAudience = ({ sessionId, token, sessionData, onComplete, onBack, setError, setLoading }) => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState(() => {
    // Initialize from existing session data if available
    if (sessionData && sessionData.audience) {
      console.log('[Step2] Restoring from sessionData:', sessionData.audience);
      return sessionData.audience;
    }
    return {
      countries: ["MY"], // Fixed to Malaysia
      states: [],
      age_ranges: [],
      gender: 'All',
      location_types: [],
      interests: [],
      languages: [],
      slang: [],
      tone_preference: 'Mix',
      audience_narrative: ''
    };
  });

  const [interestInput, setInterestInput] = useState('');
  const [slangInput, setSlangInput] = useState('');
  const [slangSuggestions, setSlangSuggestions] = useState([]);
  const [errors, setErrors] = useState({});
  const [locale, setLocale] = useState('en'); // 'en' or 'bm'

  // Field refs for error scrolling
  const refs = {
    countries: useRef(null),
    states: useRef(null),
    age_ranges: useRef(null),
    gender: useRef(null),
    location_types: useRef(null),
    interests: useRef(null),
    languages: useRef(null),
    slang: useRef(null),
    tone_preference: useRef(null)
  };

  // Error order (priority for scrolling)
  const errorOrder = [
    'countries',
    'age_ranges',
    'gender',
    'location_types',
    'interests',
    'languages',
    'tone_preference'
  ];

  // Update slang suggestions when states change (NO AUTO-ADD)
  useEffect(() => {
    if (formData.states.length > 0) {
      const suggestions = getStateSlangSuggestions(formData.states);
      setSlangSuggestions(suggestions);
    } else {
      setSlangSuggestions([]);
    }
  }, [formData.states]);

  const toggleState = (state) => {
    if (state === "All States") {
      if (formData.states.includes("All States")) {
        setFormData({ ...formData, states: [] });
      } else {
        setFormData({ ...formData, states: MALAYSIAN_STATES });
      }
    } else {
      if (formData.states.includes(state)) {
        setFormData({ ...formData, states: formData.states.filter(s => s !== state && s !== "All States") });
      } else {
        const newStates = [...formData.states.filter(s => s !== "All States"), state];
        setFormData({ ...formData, states: newStates });
      }
    }
  };

  const toggleAgeRange = (range) => {
    if (formData.age_ranges.includes(range)) {
      setFormData({ ...formData, age_ranges: formData.age_ranges.filter(r => r !== range) });
    } else {
      setFormData({ ...formData, age_ranges: [...formData.age_ranges, range] });
    }
    // Clear error when user interacts
    if (errors.age_ranges) {
      setErrors(prev => ({ ...prev, age_ranges: null }));
    }
  };

  const toggleLocationType = (type) => {
    if (formData.location_types.includes(type)) {
      setFormData({ ...formData, location_types: formData.location_types.filter(t => t !== type) });
    } else {
      setFormData({ ...formData, location_types: [...formData.location_types, type] });
    }
    if (errors.location_types) {
      setErrors(prev => ({ ...prev, location_types: null }));
    }
  };

  const toggleLanguage = (lang) => {
    if (formData.languages.includes(lang)) {
      setFormData({ ...formData, languages: formData.languages.filter(l => l !== lang) });
    } else {
      setFormData({ ...formData, languages: [...formData.languages, lang] });
    }
    if (errors.languages) {
      setErrors(prev => ({ ...prev, languages: null }));
    }
  };

  const addInterest = () => {
    if (interestInput.trim() && formData.interests.length < 5) {
      setFormData({ ...formData, interests: [...formData.interests, interestInput.trim()] });
      setInterestInput('');
      if (errors.interests) {
        setErrors(prev => ({ ...prev, interests: null }));
      }
    }
  };

  const removeInterest = (idx) => {
    setFormData({ ...formData, interests: formData.interests.filter((_, i) => i !== idx) });
  };

  // Add slang from manual input with validation
  const addSlang = () => {
    const trimmed = slangInput.trim();
    
    if (!trimmed) return;
    
    // Validate format
    if (!validateSlang(trimmed)) {
      const errorMsg = language === 'bm'
        ? 'Slang tidak sah. Gunakan 2-24 aksara (huruf, nombor, ruang, +, -, /, &, \').'
        : language === 'urban'
        ? 'Slang tidak valid. Gunakan 2-24 characters (letters, numbers, spaces, +, -, /, &, \').'
        : 'Invalid slang. Use 2-24 characters (letters, numbers, spaces, +, -, /, &, \').';
      setError(errorMsg);
      return;
    }
    
    // Check max limit
    if (formData.slang.length >= MAX_SLANG_COUNT) {
      const errorMsg = language === 'bm'
        ? `Maksimum ${MAX_SLANG_COUNT} slang tercapai.`
        : language === 'urban'
        ? `Maximum ${MAX_SLANG_COUNT} slang sudah reached.`
        : `Maximum ${MAX_SLANG_COUNT} slang reached.`;
      setError(errorMsg);
      return;
    }
    
    // Dedupe (case-insensitive)
    const lowerCaseTrimmed = trimmed.toLowerCase();
    const isDuplicate = formData.slang.some(s => s.toLowerCase() === lowerCaseTrimmed);
    
    if (isDuplicate) {
      const errorMsg = language === 'bm'
        ? 'Slang ini sudah ditambah.'
        : language === 'urban'
        ? 'Slang ini sudah added.'
        : 'This slang is already added.';
      setError(errorMsg);
      return;
    }
    
    setFormData({ ...formData, slang: [...formData.slang, trimmed] });
    setSlangInput('');
    setError(null);
  };

  // Add slang from suggestion chip
  const addSlangFromSuggestion = (suggestion) => {
    // Check max limit
    if (formData.slang.length >= MAX_SLANG_COUNT) {
      const errorMsg = language === 'bm'
        ? `Maksimum ${MAX_SLANG_COUNT} slang tercapai.`
        : language === 'urban'
        ? `Maximum ${MAX_SLANG_COUNT} slang sudah reached.`
        : `Maximum ${MAX_SLANG_COUNT} slang reached.`;
      setError(errorMsg);
      return;
    }
    
    // Dedupe (case-insensitive)
    const isDuplicate = formData.slang.some(s => s.toLowerCase() === suggestion.toLowerCase());
    if (isDuplicate) return; // Silent skip if already added
    
    setFormData({ ...formData, slang: [...formData.slang, suggestion] });
    setError(null);
  };

  const removeSlang = (idx) => {
    setFormData({ ...formData, slang: formData.slang.filter((_, i) => i !== idx) });
    setError(null);
  };

  const handleContinue = async () => {
    // Validate form
    const validationErrors = validateAudienceForm(formData);
    setErrors(validationErrors);

    // If errors exist, scroll to first error and stop
    if (Object.keys(validationErrors).length > 0) {
      scrollToFirstError({ 
        errorOrder, 
        refs, 
        errors: validationErrors, 
        headerOffset: 92 
      });
      setError(getErrorMessage('generalError', locale));
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const result = await api.setTargetedAudience(token, sessionId, formData);
      // Save both API result and form data for restoration
      onComplete({ audience: formData, audienceResult: result });
    } catch (err) {
      // Check for 401/session expiry
      if (err.message.includes('401') || err.message.includes('authentication')) {
        setError(getErrorMessage('sessionExpired', locale));
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const showStatesSelector = formData.countries.includes("MY");

  return (
    <div style={{ background: '#fff', borderRadius: '12px', padding: '20px' }}>
      <h2 style={{ fontSize: '20px', color: '#333', marginBottom: '16px' }}>Targeted Audience</h2>
      
      {/* Error Summary Bar */}
      {Object.keys(errors).length > 0 && (
        <div className="error-summary">
          {getErrorMessage('generalError', locale)}
        </div>
      )}
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Country (Fixed to Malaysia) */}
        <div ref={refs.countries}>
          <label 
            style={{ fontSize: '14px', color: '#666', display: 'block', marginBottom: '6px' }}
            data-invalid={!!errors.countries}
          >
            Country *
          </label>
          <div style={{ padding: '10px 12px', background: '#E8F4FF', border: '2px solid #0e67a2', borderRadius: '8px', fontSize: '14px', color: '#0e67a2', fontWeight: 600 }}>
            🇲🇾 Malaysia (MY)
          </div>
          {errors.countries && (
            <div className="error-text" id="countries-err">
              {getErrorMessage(errors.countries, locale)}
            </div>
          )}
        </div>

        {/* States (Multi-select - Malaysia specific) */}
        {showStatesSelector && (
          <div ref={refs.states}>
            <label 
              style={{ fontSize: '14px', color: '#666', display: 'block', marginBottom: '6px' }}
              data-invalid={!!errors.states}
            >
              States (Optional - Multi-select)
            </label>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {MALAYSIAN_STATES.map(state => (
                <button
                  key={state}
                  onClick={() => toggleState(state)}
                  className={errors.states ? 'chip-invalid' : ''}
                  tabIndex={0}
                  aria-invalid={!!errors.states}
                  style={{
                    padding: '6px 12px',
                    border: formData.states.includes(state) ? '2px solid #0e67a2' : '1px solid #ddd',
                    borderRadius: '6px',
                    background: formData.states.includes(state) ? '#E8F4FF' : '#fff',
                    color: formData.states.includes(state) ? '#0e67a2' : '#666',
                    fontSize: '13px',
                    cursor: 'pointer',
                    fontWeight: formData.states.includes(state) ? 600 : 400
                  }}
                >
                  {state === "All States" && formData.states.includes(state) ? "✓ " : ""}{state}
                </button>
              ))}
            </div>
            {formData.states.length > 0 && (
              <div style={{ fontSize: '12px', color: '#0e67a2', marginTop: '4px' }}>
                {formData.states.length} selected
              </div>
            )}
          </div>
        )}

        {/* Age Ranges (Multi-chip) */}
        <div ref={refs.age_ranges}>
          <label 
            style={{ fontSize: '14px', color: '#666', display: 'block', marginBottom: '6px' }}
            data-invalid={!!errors.age_ranges}
          >
            Age Ranges * (Multi-select)
          </label>
          <div 
            style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}
            role="group"
            aria-labelledby="age-ranges-label"
            aria-invalid={!!errors.age_ranges}
            aria-describedby={errors.age_ranges ? 'age-ranges-err' : undefined}
          >
            {AGE_RANGES.map(range => (
              <button
                key={range}
                onClick={() => toggleAgeRange(range)}
                className={errors.age_ranges ? 'chip-invalid' : ''}
                tabIndex={0}
                style={{
                  padding: '8px 14px',
                  border: formData.age_ranges.includes(range) ? '2px solid #0e67a2' : '1px solid #ddd',
                  borderRadius: '20px',
                  background: formData.age_ranges.includes(range) ? '#0e67a2' : '#fff',
                  color: formData.age_ranges.includes(range) ? '#fff' : '#666',
                  fontSize: '14px',
                  cursor: 'pointer',
                  fontWeight: formData.age_ranges.includes(range) ? 600 : 400
                }}
              >
                {range}
              </button>
            ))}
          </div>
          {errors.age_ranges && (
            <div className="error-text" id="age-ranges-err">
              {getErrorMessage(errors.age_ranges, locale)}
            </div>
          )}
        </div>

        {/* Gender */}
        <div ref={refs.gender}>
          <label style={{ fontSize: '14px', color: '#666', display: 'block', marginBottom: '6px' }}>Gender *</label>
          <div style={{ display: 'flex', gap: '8px' }}>
            {['Male', 'Female', 'All'].map(g => (
              <button
                key={g}
                onClick={() => setFormData({...formData, gender: g})}
                style={{
                  flex: 1,
                  padding: '10px',
                  border: formData.gender === g ? '2px solid #0e67a2' : '1px solid #ddd',
                  borderRadius: '8px',
                  background: formData.gender === g ? '#E8F4FF' : '#fff',
                  color: formData.gender === g ? '#0e67a2' : '#666',
                  fontSize: '14px',
                  cursor: 'pointer',
                  fontWeight: formData.gender === g ? 600 : 400
                }}
              >
                {g}
              </button>
            ))}
          </div>
        </div>

        {/* Location Types (Multi-select) */}
        <div ref={refs.location_types}>
          <label 
            style={{ fontSize: '14px', color: '#666', display: 'block', marginBottom: '6px' }}
            data-invalid={!!errors.location_types}
          >
            Location Types * (Multi-select)
          </label>
          <div 
            style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}
            role="group"
            aria-invalid={!!errors.location_types}
            aria-describedby={errors.location_types ? 'location-types-err' : undefined}
          >
            {['Urban', 'Suburban', 'Rural', 'Mixed'].map(l => (
              <button
                key={l}
                onClick={() => toggleLocationType(l)}
                className={errors.location_types ? 'chip-invalid' : ''}
                tabIndex={0}
                style={{
                  padding: '8px 14px',
                  border: formData.location_types.includes(l) ? '2px solid #0e67a2' : '1px solid #ddd',
                  borderRadius: '8px',
                  background: formData.location_types.includes(l) ? '#E8F4FF' : '#fff',
                  color: formData.location_types.includes(l) ? '#0e67a2' : '#666',
                  fontSize: '13px',
                  cursor: 'pointer',
                  fontWeight: formData.location_types.includes(l) ? 600 : 400
                }}
              >
                {l}
              </button>
            ))}
          </div>
          {errors.location_types && (
            <div className="error-text" id="location-types-err">
              {getErrorMessage(errors.location_types, locale)}
            </div>
          )}
        </div>

        {/* Interests */}
        <div ref={refs.interests}>
          <label 
            style={{ fontSize: '14px', color: '#666', display: 'block', marginBottom: '6px' }}
            data-invalid={!!errors.interests}
          >
            Interests * (1-5 items)
          </label>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
            <input
              value={interestInput}
              onChange={(e) => setInterestInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addInterest()}
              placeholder="e.g., Health, Fitness, Islamic"
              className={errors.interests ? 'is-invalid' : ''}
              aria-invalid={!!errors.interests}
              aria-describedby={errors.interests ? 'interests-err' : undefined}
              style={{
                flex: 1,
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
            <button
              onClick={addInterest}
              disabled={formData.interests.length >= 5}
              style={{
                padding: '10px 16px',
                background: formData.interests.length < 5 ? '#0e67a2' : '#ccc',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: formData.interests.length < 5 ? 'pointer' : 'not-allowed',
                fontWeight: 600
              }}
            >
              +
            </button>
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {formData.interests.map((int, idx) => (
              <div
                key={`item-${idx}`}
                style={{
                  padding: '6px 12px',
                  background: '#E8F4FF',
                  color: '#0e67a2',
                  borderRadius: '6px',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {int}
                <span onClick={() => removeInterest(idx)} style={{ cursor: 'pointer', fontWeight: 'bold' }}>×</span>
              </div>
            ))}
          </div>
          {errors.interests && (
            <div className="error-text" id="interests-err">
              {getErrorMessage(errors.interests, locale)}
            </div>
          )}
        </div>

        {/* Languages (Multi-select) */}
        <div ref={refs.languages}>
          <label 
            style={{ fontSize: '14px', color: '#666', display: 'block', marginBottom: '6px' }}
            data-invalid={!!errors.languages}
          >
            Languages * (Multi-select)
          </label>
          <div 
            style={{ display: 'flex', gap: '8px' }}
            role="group"
            aria-invalid={!!errors.languages}
            aria-describedby={errors.languages ? 'languages-err' : undefined}
          >
            {['BM', 'EN', 'Mix'].map(l => (
              <button
                key={l}
                onClick={() => toggleLanguage(l)}
                className={errors.languages ? 'chip-invalid' : ''}
                tabIndex={0}
                style={{
                  flex: 1,
                  padding: '10px',
                  border: formData.languages.includes(l) ? '2px solid #0e67a2' : '1px solid #ddd',
                  borderRadius: '8px',
                  background: formData.languages.includes(l) ? '#E8F4FF' : '#fff',
                  color: formData.languages.includes(l) ? '#0e67a2' : '#666',
                  fontSize: '14px',
                  cursor: 'pointer',
                  fontWeight: formData.languages.includes(l) ? 600 : 400
                }}
              >
                {l}
              </button>
            ))}
          </div>
          {errors.languages && (
            <div className="error-text" id="languages-err">
              {getErrorMessage(errors.languages, locale)}
            </div>
          )}
        </div>

        {/* Slang (State-specific suggestions - manual tap to add) */}
        <div ref={refs.slang}>
          <label style={{ fontSize: '14px', color: '#666', display: 'block', marginBottom: '6px', fontWeight: 600 }}>
            {language === 'bm' 
              ? 'Slang (Cadangan ikut negeri — tap untuk tambah, boleh edit)'
              : language === 'urban'
              ? 'Slang (Cadangan berdasarkan negeri — tap untuk add, boleh edit)'
              : 'Slang (State suggestions — tap to add, editable)'}
          </label>
          <div style={{ fontSize: '12px', color: '#999', marginBottom: '10px' }}>
            {language === 'bm'
              ? 'Cadangan berdasarkan negeri yang anda pilih. Anda juga boleh tambah sendiri.'
              : language === 'urban'
              ? 'Suggestions berdasarkan negeri yang anda pilih. Anda juga boleh add sendiri.'
              : 'Suggestions based on selected states. You can also add custom slang.'}
          </div>

          {/* Slang Suggestions Tray (Tap to add) */}
          {slangSuggestions.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '11px', color: '#666', marginBottom: '6px', fontWeight: 600 }}>
                {language === 'bm' 
                  ? 'Cadangan (tap untuk tambah):'
                  : language === 'urban'
                  ? 'Suggestions (tap untuk add):'
                  : 'Suggestions (tap to add):'}
              </div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {slangSuggestions.map((suggestion, idx) => {
                  const isAdded = formData.slang.some(s => s.toLowerCase() === suggestion.toLowerCase());
                  return (
                    <button
                      key={`item-${idx}`}
                      onClick={() => !isAdded && addSlangFromSuggestion(suggestion)}
                      disabled={isAdded || formData.slang.length >= MAX_SLANG_COUNT}
                      style={{
                        padding: '6px 12px',
                        background: isAdded ? '#E8E8E8' : '#F0F8FF',
                        color: isAdded ? '#999' : '#0e67a2',
                        border: isAdded ? '1px solid #ccc' : '1px solid #0e67a2',
                        borderRadius: '16px',
                        fontSize: '12px',
                        cursor: isAdded || formData.slang.length >= MAX_SLANG_COUNT ? 'not-allowed' : 'pointer',
                        fontWeight: 500,
                        opacity: isAdded ? 0.5 : 1,
                        transition: 'all 0.2s'
                      }}
                    >
                      {isAdded ? `✓ ${suggestion}` : suggestion}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Manual input */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
            <input
              value={slangInput}
              onChange={(e) => setSlangInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addSlang();
                }
              }}
              placeholder={language === 'bm' ? 'Tambah slang tersuai...' : language === 'urban' ? 'Add custom slang...' : 'Add custom slang...'}
              disabled={formData.slang.length >= MAX_SLANG_COUNT}
              style={{
                flex: 1,
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '14px',
                opacity: formData.slang.length >= MAX_SLANG_COUNT ? 0.5 : 1
              }}
            />
            <button
              onClick={addSlang}
              disabled={formData.slang.length >= MAX_SLANG_COUNT}
              style={{
                padding: '10px 16px',
                background: formData.slang.length >= MAX_SLANG_COUNT ? '#ccc' : '#0e67a2',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: formData.slang.length >= MAX_SLANG_COUNT ? 'not-allowed' : 'pointer',
                fontWeight: 600
              }}
            >
              +
            </button>
          </div>

          {/* Selected slang chips */}
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' }}>
            {formData.slang.map((s, idx) => (
              <div
                key={`item-${idx}`}
                style={{
                  padding: '6px 12px',
                  background: '#FFF4E6',
                  color: '#F7941E',
                  border: '1px solid #F7941E',
                  borderRadius: '6px',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontWeight: 500
                }}
              >
                {s}
                <span onClick={() => removeSlang(idx)} style={{ cursor: 'pointer', fontWeight: 'bold', fontSize: '16px' }}>×</span>
              </div>
            ))}
          </div>

          {/* Max limit indicator */}
          {formData.slang.length >= MAX_SLANG_COUNT && (
            <div style={{ fontSize: '11px', color: '#E53935', marginTop: '4px' }}>
              {language === 'bm'
                ? `⚠️ Maksimum ${MAX_SLANG_COUNT} slang tercapai.`
                : language === 'urban'
                ? `⚠️ Maximum ${MAX_SLANG_COUNT} slang sudah reached.`
                : `⚠️ Maximum ${MAX_SLANG_COUNT} slang reached.`}
            </div>
          )}
        </div>

        {/* Tone Preference */}
        <div ref={refs.tone_preference}>
          <label style={{ fontSize: '14px', color: '#666', display: 'block', marginBottom: '6px' }}>
            Tone Preference *
          </label>
          <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
            {[
              { value: 'Santai+', label: 'Santai+ (casual, friendly, sedikit gurau)' },
              { value: 'Formal', label: 'Formal (kemas, beradab, korporat)' },
              { value: 'Mix', label: 'Mix (seimbang santai & formal)' }
            ].map(tone => (
              <button
                key={tone.value}
                onClick={() => setFormData({...formData, tone_preference: tone.value})}
                style={{
                  padding: '12px',
                  border: formData.tone_preference === tone.value ? '2px solid #0e67a2' : '1px solid #ddd',
                  borderRadius: '8px',
                  background: formData.tone_preference === tone.value ? '#E8F4FF' : '#fff',
                  color: formData.tone_preference === tone.value ? '#0e67a2' : '#666',
                  fontSize: '14px',
                  cursor: 'pointer',
                  fontWeight: formData.tone_preference === tone.value ? 600 : 400,
                  textAlign: 'left'
                }}
              >
                {tone.label}
              </button>
            ))}
          </div>
        </div>

        {/* Audience Narrative */}
        <div>
          <label style={{ fontSize: '14px', color: '#666', display: 'block', marginBottom: '6px' }}>
            Audience Narrative (Optional)
          </label>
          <textarea
            value={formData.audience_narrative}
            onChange={(e) => setFormData({...formData, audience_narrative: e.target.value})}
            placeholder="e.g., Mak-mak 40an yang risau kesihatan anak-anak"
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '8px',
              fontSize: '14px',
              minHeight: '60px',
              fontFamily: 'inherit'
            }}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
        <button
          onClick={onBack}
          style={{
            flex: 1,
            padding: '14px',
            background: '#fff',
            border: '2px solid #0e67a2',
            borderRadius: '12px',
            color: '#0e67a2',
            fontSize: '15px',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          Back
        </button>
        <button
          onClick={handleContinue}
          style={{
            flex: 2,
            padding: '14px',
            background: '#F7941E',
            border: 'none',
            borderRadius: '12px',
            color: '#fff',
            fontSize: '15px',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default Step2TargetedAudience;
